import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Icon,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import SectionHeader from "./SectionHeader";
import { useDispatch } from "react-redux";
import { downloadFileCommon } from "../../../store/actions/commonAction";

export default function AgingAnalysisModule({ data, agingAlerts }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const critical = useMemo(
    () => data?.agingAlerts?.find((i) => i.severity === "high"),
    [data],
  );

  const warning = useMemo(
    () => data?.agingAlerts?.find((i) => i.severity === "medium"),
    [data],
  );

  const attention = useMemo(
    () => data?.agingAlerts?.find((i) => i.severity === "attention"),
    [data],
  );

  const downloadExcel = () => {
    // Implement the logic to download the data as an Excel file
    console.log("Download Excel clicked");
    let excelData = data?.aging?.map((supplier) => ({
      "Invoice Number": supplier.invoiceNumber,
      Supplier: supplier.supplier,
      "Invoice Date": supplier.invoiceDate,
      "Invoice Quantity": supplier.invoiceQuantity,
      "Aging Days": supplier.agingDays,
      "Aging Bucket": supplier.agingBucket?.label,
      "Pending Value": supplier.pendingValue,
    }));
    dispatch(
      downloadFileCommon({
        fileName: "aging_analysis_report.xlsx",
        query: {},
        body: { data: excelData, docType: "aging_analysis_report" },
        path: "/document/common/excel",
      }),
    );
  };

  console.log("AgingAnalysisModule data", critical, warning, attention, data);
  return (
    <Box style={{ marginTop: 16 }}>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12}>
          {(critical || warning || attention) && (
            <Card
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                mb: 3,
              }}
            >
              <SectionHeader title="Exception Alerts" />
              <Stack direction="row" spacing={1}>
                {critical && (
                  <Alert
                    severity="error"
                    icon={<Icon>warning</Icon>}
                    sx={{ borderRadius: 2 }}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      Critical -- {critical?.count} invoice
                      {critical?.count > 1 ? "s" : ""} aged more than 30 days
                    </Typography>
                    <Typography variant="caption" display="block">
                      {critical?.invoices?.join("  |  ")}
                    </Typography>
                    <Typography variant="caption">
                      Total pending value: {critical?.pendingValue} -- Immediate
                      supplier follow-up required
                    </Typography>
                  </Alert>
                )}
                {warning && (
                  <Alert
                    severity="medium"
                    icon={<Icon>schedule</Icon>}
                    sx={{ borderRadius: 2 }}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      Warning -- {warning?.count} invoice
                      {warning?.count > 1 ? "s" : ""} aged 16-30 days
                    </Typography>
                    <Typography variant="caption" display="block">
                      {warning?.invoices?.join("  |  ")}
                    </Typography>
                    <Typography variant="caption">
                      Total pending value: {warning?.pendingValue} -- Escalation
                      recommended
                    </Typography>
                  </Alert>
                )}
                {attention && (
                  <Alert
                    severity="info"
                    icon={<Icon>timelapse</Icon>}
                    sx={{ borderRadius: 2 }}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      Attention -- {attention?.count} invoice
                      {attention?.count > 1 ? "s" : ""} partially received
                      within 15 days
                    </Typography>
                    <Typography variant="caption" display="block">
                      {attention?.invoices?.join("  |  ")}
                    </Typography>
                    <Typography variant="caption">
                      Pending qty: {attention?.pendingQty} units -- Monitor
                      receipt progress
                    </Typography>
                  </Alert>
                )}
              </Stack>
            </Card>
          )}
        </Grid>
      </Grid>

      <Card
        elevation={0}
        sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}
      >
        <Box px={2.5} pt={2}>
          <SectionHeader
            title="Open Invoice Register"
            subtitle="Sorted by aging days (descending) -- responds to filters above"
            action={
              <Button
                size="small"
                variant="outlined"
                startIcon={<Icon fontSize="small">file_download</Icon>}
                sx={{ textTransform: "none" }}
                onClick={downloadExcel}
              >
                Export Excel
              </Button>
            }
          />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.error.main, 0.05),
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    py: 1.2,
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell>Invoice No.</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Invoice Date</TableCell>
                <TableCell align="right">Inv. Qty</TableCell>
                <TableCell align="right">Aging Days</TableCell>
                <TableCell align="center">Aging Bucket</TableCell>
                <TableCell align="right">Pending Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.aging?.map((inv, index) => (
                <TableRow
                  key={"inv-" + index}
                  hover
                  sx={{
                    bgcolor:
                      inv.agingDays > 30
                        ? alpha(theme.palette.error.main, 0.03)
                        : undefined,
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="primary.main"
                    >
                      {inv.invoiceNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontWeight={600}>
                      {inv.supplier}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{inv.invoiceDate}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption">
                      {inv.invoiceQuantity}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="caption"
                      fontWeight={800}
                      sx={{
                        color:
                          inv.agingBucket?.severity === "high"
                            ? theme.palette.error.main
                            : inv.agingBucket?.severity === "medium"
                              ? theme.palette.warning.main
                              : theme.palette.text.primary,
                      }}
                    >
                      {inv.agingDays}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={inv.agingBucket?.label}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: 10,
                        bgcolor: alpha(
                          inv.agingBucket?.severity === "high"
                            ? theme.palette.error.main
                            : inv.agingBucket?.severity === "medium"
                              ? theme.palette.warning.main
                              : theme.palette.text.primary,
                          0.12,
                        ),
                        color:
                          inv.agingBucket?.severity === "high"
                            ? theme.palette.error.main
                            : inv.agingBucket?.severity === "medium"
                              ? theme.palette.warning.main
                              : theme.palette.text.primary,
                        border: `1px solid ${alpha(
                          inv.agingBucket?.severity === "high"
                            ? theme.palette.error.main
                            : inv.agingBucket?.severity === "medium"
                              ? theme.palette.warning.main
                              : theme.palette.text.primary,
                          0.3,
                        )}`,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color={theme.palette.primary.main}
                    >
                      {inv.pendingValue}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
